import { type FirebaseApp, type FirebaseOptions, getApps, initializeApp } from 'firebase/app';
import {
  type AppCheck,
  getToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';

const APP_CHECK_WINDOW_KEY = '__buyerAppFirebaseAppCheck';

let appCheckInitPromise: Promise<AppCheck | null> | null = null;

function getCachedAppCheck(): AppCheck | null {
  if (typeof window !== 'undefined') {
    return (window as Window & { [APP_CHECK_WINDOW_KEY]?: AppCheck })[APP_CHECK_WINDOW_KEY] ?? null;
  }
  return null;
}

function setCachedAppCheck(instance: AppCheck): void {
  if (typeof window !== 'undefined') {
    (window as Window & { [APP_CHECK_WINDOW_KEY]?: AppCheck })[APP_CHECK_WINDOW_KEY] = instance;
  }
}

function initializeFirebaseApp(): FirebaseApp | null {
  try {
    const configJson = process.env.NEXT_PUBLIC_FIREBASE_CONFIG;
    if (!configJson) {
      console.warn('Firebase config is not configured.');
      return null;
    }

    const config = JSON.parse(configJson) as FirebaseOptions;
    const existingApps = getApps();
    if (existingApps.length > 0) {
      return existingApps[0]!;
    }

    return initializeApp(config);
  } catch (error) {
    console.warn('Failed to initialize Firebase app:', error);
    return null;
  }
}

function ensureAppCheck(): Promise<AppCheck | null> {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  if (appCheckInitPromise) {
    return appCheckInitPromise;
  }

  appCheckInitPromise = (async () => {
    try {
      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      if (!siteKey) {
        console.warn('reCAPTCHA site key is not configured.');
        return null;
      }

      const cached = getCachedAppCheck();
      if (cached) {
        return cached;
      }

      const app = initializeFirebaseApp();
      if (!app) {
        return null;
      }

      if (process.env.NODE_ENV === 'development') {
        (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      }

      const appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
      setCachedAppCheck(appCheck);
      return appCheck;
    } catch (error) {
      console.warn('Failed to initialize Firebase App Check:', error);
      return null;
    }
  })();

  return appCheckInitPromise;
}

export async function getAppCheckToken(): Promise<string | null> {
  try {
    const appCheck = await ensureAppCheck();
    if (!appCheck) {
      return null;
    }

    const { token } = await getToken(appCheck, false);
    return token;
  } catch (error) {
    console.warn('Failed to get Firebase App Check token:', error);
    return null;
  }
}
